import React, { useCallback, useEffect, useState } from 'react'
import { t } from 'i18next'
import { useParams } from 'react-router-dom'
import BootstrapTable from 'react-bootstrap-table-next'
import { useActivitiesState } from '../../../stores/Configuration/ActivitiesProvider'
import UniversalTableWrapperTest from '../../Table/UniversalTableWrapperTest'
import { useTableState } from '../../Table/TableProvider'
import { useAssetTypesState } from '../../../stores/Configuration/AssetTypesProvider'
import SearchField from '../../Forms/SearchField'

const AssetTypeItemActivities = ({ onSubmitReady, onUserInteraction }) => {
  const {
    assignedActivities,
    setAssignedActivities,
    assignedActivitiesHasChanged,
    putAssignedActivities,
    loadingStates,
  } = useAssetTypesState()
  const { activities } = useActivitiesState()
  const { tableRef } = useTableState()
  const [checkedActivities, setCheckedActivities] = useState([])
  const { assetTypeId } = useParams()
  const [searchText, setSearchText] = useState('')
  const [filteredActivities, setFilteredActivities] = useState([])

  // [{ id: "", isAssigned: bool, displayName: "" }]
  useEffect(() => {
    if (activities && assignedActivities) {
      const newCheckedActivities = []
      for (const obj of activities) {
        newCheckedActivities.push({
          id: obj.id,
          isAssigned: assignedActivities.includes(obj.id),
          displayName: obj.displayName,
        })
      }
      setCheckedActivities(newCheckedActivities)
    }
  }, [activities, assignedActivities])

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredActivities(checkedActivities)
    } else {
      setFilteredActivities(
        checkedActivities.filter((a) =>
          a.displayName.toLowerCase().includes(searchText.toLowerCase())
        )
      )
    }
  }, [checkedActivities, searchText])

  const handleSearch = (value) => {
    setSearchText(value)
  }
  const handleClearClick = () => {
    setSearchText('')
    setFilteredActivities(checkedActivities)
  }

  const handleSubmit = useCallback(() => {
    putAssignedActivities(assetTypeId, assignedActivities)
  }, [assetTypeId, assignedActivities, putAssignedActivities])

  useEffect(() => {
    if (onSubmitReady) {
      onSubmitReady({
        handleSubmit,
        isChanged: assignedActivitiesHasChanged,
        isLoading: loadingStates.putAssignedActivities,
      })
    }
  }, [
    assignedActivitiesHasChanged,
    loadingStates.putAssignedActivities,
    onSubmitReady,
    handleSubmit,
  ])

  const handleClick = () => {
    if (onUserInteraction) onUserInteraction()

    const filteredIds = filteredActivities.map((a) => a.id)
    const allSelected = filteredIds.every((id) =>
      assignedActivities.includes(id)
    )
    if (allSelected) {
      setAssignedActivities((prev) =>
        prev.filter((id) => !filteredIds.includes(id))
      )
      setCheckedActivities((prev) =>
        prev.map((a) =>
          filteredIds.includes(a.id) ? { ...a, isAssigned: false } : a
        )
      )
      setFilteredActivities((prev) =>
        prev.map((a) =>
          filteredIds.includes(a.id) ? { ...a, isAssigned: false } : a
        )
      )
    } else {
      setAssignedActivities((prev) => {
        const newSet = new Set([...prev, ...filteredIds])
        return Array.from(newSet)
      })
      setCheckedActivities((prev) =>
        prev.map((a) =>
          filteredIds.includes(a.id) ? { ...a, isAssigned: true } : a
        )
      )
      setFilteredActivities((prev) =>
        prev.map((a) =>
          filteredIds.includes(a.id) ? { ...a, isAssigned: true } : a
        )
      )
    }
  }

  const columns = [
    {
      dataField: 'isAssigned',
      text: (
        <input
          type='checkbox'
          id='selectAllCheckbox'
          className=' mx-auto my-1 fs-6'
          style={{ transform: 'scale(1.2)' }}
          checked={
            filteredActivities.length > 0 &&
            filteredActivities.every((a) => assignedActivities.includes(a.id))
          }
          onChange={handleClick}
        />
      ),
      sort: false,
      formatter: (cell, row) => (
        <input
          type='checkbox'
          checked={cell}
          aria-label={t('assetTypes:ifChecked')}
          onChange={() => {}}
          id={row.id}
        />
      ),
      headerAlign: 'center',
      align: 'center',
      headerStyle: { width: '160px' },
    },
    {
      dataField: 'displayName',
      text: t('assetTypes:assignedActivity'),
      sort: true,
    },
  ]

  const handleChangeCheckBox = (id) => {
    if (onUserInteraction) onUserInteraction()
    var newValues = [...assignedActivities]
    if (assignedActivities.includes(id)) {
      let index = assignedActivities.indexOf(id)
      newValues.splice(index, 1)
    } else {
      newValues.push(id)
    }
    setAssignedActivities(newValues)
  }

  const onRowClick = (cell, row, rowIndex) => {
    handleChangeCheckBox(row.id)
  }

  return (
    <div>
      <UniversalTableWrapperTest
        tableIsLoading={loadingStates.getAssignedActivities}
      >
        <SearchField
          type='search'
          placeholder={t('assetTypes:searchInputPlaceholder')}
          value={searchText}
          className='mb-2'
          onChange={(e) => handleSearch(e.target.value)}
          onClearClick={handleClearClick}
        />

        <BootstrapTable
          ref={tableRef}
          noDataIndication={() => (
            <div className='no-data-placeholder'>
              {!loadingStates.getAssignedActivities &&
                t('assetTypes:noActivitiesAvailable')}
            </div>
          )}
          loading={loadingStates.getAssignedActivities}
          bootstrap4
          keyField='id'
          hover
          data={filteredActivities}
          columns={columns}
          bordered={false}
          rowEvents={{ onClick: onRowClick }}
        />
      </UniversalTableWrapperTest>
    </div>
  )
}
export default AssetTypeItemActivities
